import React from 'react';
import { Badge } from './ui/badge';
import { usePortfolio } from '../context/PortfolioContext';

const Skills = () => {
  const { portfolioData, loading } = usePortfolio();
  
  if (loading || !portfolioData) {
    return null;
  }
  
  const { skills } = portfolioData;

  // Group skills by category
  const groupedSkills = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {});

  const categoryConfig = {
    Frontend: { 
      gradient: 'from-orange-500 to-amber-500',
      badge: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      icon: 'react'
    },
    Backend: { 
      gradient: 'from-amber-500 to-orange-500',
      badge: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      icon: 'server'
    },
    Database: { 
      gradient: 'from-orange-400 to-orange-600',
      badge: 'bg-orange-400/10 text-orange-400 border-orange-400/20',
      icon: 'database'
    },
    Tools: { 
      gradient: 'from-orange-600 to-amber-600',
      badge: 'bg-orange-600/10 text-orange-600 border-orange-600/20',
      icon: 'tool'
    }
  };

  return (
    <section id="skills" data-story-section className="py-32 bg-zinc-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,107,53,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,107,53,0.02)_1px,transparent_1px)] bg-[size:100px_100px]"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-20 space-y-4">
          <h2 className="text-sm font-semibold text-orange-500 uppercase tracking-wider">Technical Arsenal</h2>
          <h3 className="text-5xl md:text-6xl font-bold text-white">
            Skills & <span className="gradient-text">Technologies</span>
          </h3>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            A comprehensive toolkit for building modern, scalable web applications
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {Object.entries(groupedSkills).map(([category, categorySkills]) => {
            const config = categoryConfig[category];
            if (!config) {
              console.warn(`No config found for category: ${category}`);
              return null;
            }
            return (
              <div key={category} className="glass parallax-card p-8 rounded-3xl border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 group hover:translate-y-[-8px]">
                <div className="mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${config.gradient} mb-4`}>
                    <span className="text-white font-bold text-2xl">{config.icon.substring(0, 2).toUpperCase()}</span>
                  </div>
                  <h4 className="text-2xl font-bold text-white flex items-center gap-2 group-hover:text-orange-500 transition-colors">
                    {category}
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {categorySkills.map((skill, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className={`${config.badge} border px-3 py-1.5 font-medium hover:scale-105 transition-transform duration-200 cursor-default`}
                    >
                      {skill.name}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Decorative glowing orbs */}
        <div className="absolute top-1/4 left-10 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-1/4 right-10 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl animate-pulse-glow animation-delay-2000"></div>
      </div>
    </section>
  );
};

export default Skills;
